import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    FunctionField,
    BooleanField,
    DateField,
    EditButton,
    ShowButton,
    Filter,
    TextInput,
    SelectInput,
    BooleanInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    usePermissions,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Chip,
    Box,
    Typography,
    Tooltip,
    useMediaQuery,
    Fab,
} from '@mui/material';
import type { Theme } from '@mui/material';
import {
    CheckCircle,
    Cancel,
    Star,
    Speed,
    Settings,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const OCPPTemplatesFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.ocpp-templates.filters.search')}
                source="search"
                alwaysOn
            />
            <SelectInput
                source="ocppVersion"
                label={translate('resources.ocpp-templates.fields.ocppVersion')}
                choices={[
                    { id: '1.6', name: translate('resources.ocpp-templates.ocppVersions.1.6') },
                    { id: '2.0.1', name: translate('resources.ocpp-templates.ocppVersions.2.0.1') },
                ]}
            />
            <BooleanInput
                label={translate('resources.ocpp-templates.filters.activeOnly')}
                source="isActive"
            />
            <BooleanInput
                label={translate('resources.ocpp-templates.filters.defaultOnly')}
                source="isDefault"
            />
        </Filter>
    );
};

const ListActions = () => {
    const { permissions } = usePermissions();
    const canCreate = permissions?.includes('ocpp-templates:write:all');

    return (
        <TopToolbar>
            {canCreate && <CreateButton />}
            <ExportButton />
            <RefreshButton />
        </TopToolbar>
    );
};

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
        <RefreshButton />
    </Box>
);

const MobileTemplateList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const translate = useTranslate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/ocpp-templates/${record.id}`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer', '&:active': { backgroundColor: '#F5F5F5' } }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                                {Boolean(record.isDefault) && <Star sx={{ fontSize: 16, color: '#FFA000', flexShrink: 0 }} />}
                                <Typography sx={{ fontWeight: record.isDefault ? 600 : 500, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {record.templateName}
                                </Typography>
                            </Box>
                            <Chip
                                label={record.isActive ? translate('status.active') : translate('status.inactive')}
                                size="small"
                                sx={{
                                    flexShrink: 0,
                                    ml: 1,
                                    backgroundColor: record.isActive ? '#E8F5E8' : '#F5F5F5',
                                    color: record.isActive ? '#009A75' : '#757575',
                                    fontSize: '0.7rem',
                                    height: 24,
                                }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                            <Chip
                                label={translate(`resources.ocpp-templates.ocppVersions.${record.ocppVersion}`)}
                                size="small"
                                sx={{ backgroundColor: '#E3F2FD', color: '#1976D2', fontSize: '0.7rem', height: 22 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {record.normalIntervalSeconds}s
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {record.chargerCount || 0} {translate('resources.ocpp-templates.chargersPlural')}
                            </Typography>
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const OCPPTemplatesList = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const canEdit = permissions?.includes('ocpp-templates:write:all');
    const canCreate = permissions?.includes('ocpp-templates:write:all');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={<OCPPTemplatesFilter />}
                actions={isMobile ? <MobileListActions /> : <ListActions />}
                empty={isMobile ? false : undefined}
                perPage={25}
                sort={{ field: 'templateName', order: 'ASC' }}
                sx={{
                    ...(isMobile ? {
                        '& .RaList-content': {
                            backgroundColor: 'transparent !important',
                            boxShadow: 'none !important',
                            mt: 2,
                        },
                        '& .RaList-actions': {
                            backgroundColor: 'transparent !important',
                            p: '0 !important',
                            minHeight: 'auto !important',
                        },
                        '& .RaListToolbar-root': {
                            backgroundColor: 'transparent !important',
                            flexDirection: 'row !important',
                            flexWrap: 'wrap !important',
                            alignItems: 'center !important',
                            gap: '8px !important',
                        },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': {
                            flex: '1 1 100% !important',
                            width: '100% !important',
                        },
                        '& .filter-field, & .RaFilterForm-filterFormInput': {
                            width: '100% !important',
                        },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': {
                            backgroundColor: 'transparent !important',
                            minHeight: 'auto !important',
                        },
                    } : {
                        '& .RaList-content': {
                            backgroundColor: '#FAFAFA',
                        },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileTemplateList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 600,
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <FunctionField
                            source="templateName"
                            label={translate('resources.ocpp-templates.fields.templateName')}
                            render={(record: any) => {
                                if (!record) return null;
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {Boolean(record.isDefault) && (
                                            <Tooltip title={translate('resources.ocpp-templates.defaultTemplate')}>
                                                <Star sx={{ fontSize: 16, color: '#FFA000' }} />
                                            </Tooltip>
                                        )}
                                        <Typography variant="body2" sx={{ fontWeight: Boolean(record.isDefault) ? 600 : 400 }}>
                                            {record.templateName || ''}
                                        </Typography>
                                    </Box>
                                );
                            }}
                        />

                        <TextField
                            source="description"
                            label={translate('resources.ocpp-templates.fields.description')}
                            sx={{
                                '& .RaTextField-root': {
                                    maxWidth: '300px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                },
                            }}
                        />

                        <FunctionField
                            source="ocppVersion"
                            label={translate('resources.ocpp-templates.fields.ocppVersion')}
                            render={(record: any) => (
                                <Chip
                                    label={translate(`resources.ocpp-templates.ocppVersions.${record.ocppVersion}`)}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#E3F2FD',
                                        color: '#1976D2',
                                        fontWeight: 500,
                                    }}
                                />
                            )}
                        />

                        <FunctionField
                            source="normalIntervalSeconds"
                            label={translate('resources.ocpp-templates.fields.normalIntervalSeconds')}
                            render={(record: any) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Speed sx={{ fontSize: 16, color: '#666' }} />
                                    <Typography variant="body2">
                                        {record.normalIntervalSeconds}s
                                    </Typography>
                                </Box>
                            )}
                        />

                        <FunctionField
                            label={translate('resources.ocpp-templates.configuration')}
                            render={(record: any) => (
                                <Typography variant="caption" color="text.secondary">
                                    {translate('resources.ocpp-templates.configExplanation', {
                                        normal: record.normalIntervalSeconds,
                                        approaching: record.approachingIntervalSeconds,
                                        approachingThreshold: record.approachingThresholdSeconds,
                                        near: record.nearIntervalSeconds,
                                        nearThreshold: record.nearThresholdSeconds,
                                    })}
                                </Typography>
                            )}
                        />

                        <FunctionField
                            source="chargerCount"
                            label={translate('resources.ocpp-templates.fields.usage')}
                            render={(record: any) => {
                                const count = record.chargerCount || 0;
                                return (
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {count}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {count === 1 ? translate('resources.ocpp-templates.charger') : translate('resources.ocpp-templates.chargersPlural')}
                                        </Typography>
                                    </Box>
                                );
                            }}
                        />

                        <BooleanField
                            source="isActive"
                            label={translate('resources.ocpp-templates.fields.isActive')}
                            valueLabelTrue={translate('status.active')}
                            valueLabelFalse={translate('status.inactive')}
                            TrueIcon={CheckCircle}
                            FalseIcon={Cancel}
                            sx={{
                                '& .RaBooleanField-true': {
                                    color: '#009A75',
                                },
                                '& .RaBooleanField-false': {
                                    color: '#F44336',
                                },
                            }}
                        />

                        <DateField
                            source="createdAt"
                            label={translate('resources.ocpp-templates.fields.createdAt')}
                            options={{
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            }}
                        />

                        <FunctionField
                            label={translate('ra.action.actions')}
                            render={(record: any) => (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <ShowButton label="" />
                                    {canEdit && <EditButton label="" />}
                                    {canEdit && !record.isDefault && (
                                        <Tooltip title={translate('resources.ocpp-templates.actions.setAsDefault')}>
                                            <Settings
                                                sx={{
                                                    fontSize: 20,
                                                    color: 'rgba(0, 0, 0, 0.54)',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        color: 'rgba(0, 0, 0, 0.87)',
                                                    },
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    console.log('Set as default:', record.id);
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>
                            )}
                        />
                    </Datagrid>
                )}
            </List>
            {isMobile && canCreate && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/ocpp-templates/create')}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#009A75',
                        '&:hover': { backgroundColor: '#007A5E' },
                        zIndex: 1100,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};
